<template>
  <div class="notif-bell" v-click-outside="close">
    <button
      type="button"
      class="notif-bell__btn"
      :aria-label="unread ? `Notifications, ${unread} unread` : 'Notifications'"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
    >
      <i class="las la-bell"></i>
      <span v-if="unread" class="notif-bell__badge">{{ unread > 99 ? '99+' : unread }}</span>
    </button>

    <div v-if="open" class="notif-panel" role="dialog" aria-label="Notifications">
      <div class="notif-panel__head">
        <strong>
          Notifications
          <span v-if="branchName" class="notif-panel__branch">{{ branchName }}</span>
        </strong>
        <button v-if="unread" type="button" class="notif-link" @click="markAllRead">Mark all read</button>
      </div>

      <div class="notif-panel__body">
        <div v-if="loading && !recent.length" class="notif-empty">Loading…</div>
        <div v-else-if="!recent.length" class="notif-empty">
          <i class="las la-bell-slash"></i>
          You're all caught up.
        </div>
        <a
          v-for="n in recent"
          :key="n.id"
          href="#"
          class="notif-item"
          :class="{ 'is-unread': !n.read }"
          @click.prevent="openItem(n)"
        >
          <span class="notif-dot" :class="`lvl-${n.level}`"></span>
          <span class="notif-item__main">
            <span class="notif-item__title">{{ n.title }}</span>
            <span v-if="n.body" class="notif-item__text">{{ n.body }}</span>
            <span class="notif-item__meta">{{ n.sender || 'System' }} · {{ ago(n.created_at) }}</span>
          </span>
        </a>
      </div>

      <router-link to="/notifications" class="notif-panel__foot" @click.native="close">
        View all notifications
      </router-link>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'

export default {
  name: 'NotificationBell',

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._outside = (e) => { if (!el.contains(e.target)) binding.value(e) }
        document.addEventListener('click', el._outside, true)
      },
      unbind(el) {
        document.removeEventListener('click', el._outside, true)
      },
    },
  },

  data: () => ({ open: false, loading: false }),

  computed: {
    ...mapState('notifications', ['unread', 'recent', 'recentLoaded', 'branchName']),
  },

  watch: {
    '$route.path'() { this.close() },
  },

  methods: {
    async toggle() {
      this.open = !this.open
      if (this.open) {
        this.loading = true
        try { await this.$store.dispatch('notifications/fetchRecent') } catch { /* keep cached list */ }
        this.loading = false
      }
    },
    close() { this.open = false },
    markAllRead() { this.$store.dispatch('notifications/markAllRead') },
    openItem(n) {
      if (!n.read) this.$store.dispatch('notifications/markRead', n.id)
      this.close()
      this.$router.push(n.link || '/notifications').catch(() => {})
    },
    ago(iso) { return iso ? moment(iso).fromNow() : '' },
  },
}
</script>

<style scoped>
.notif-bell { position: relative; display: inline-flex; }

.notif-bell__btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 22px;
  cursor: pointer;
}
.notif-bell__btn:hover { background: rgba(255, 255, 255, 0.24); }

.notif-bell__badge {
  position: absolute;
  top: -2px;
  right: -4px;
  min-width: 19px;
  height: 19px;
  padding: 0 5px;
  border-radius: 10px;
  background: #ffd23f;
  color: #4a1a00;
  font-size: 11px;
  font-weight: 800;
  line-height: 19px;
  text-align: center;
  box-shadow: 0 0 0 2px #e31e25;
}

.notif-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 360px;
  max-width: calc(100vw - 24px);
  background: #fff;
  color: #1f2430;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(20, 20, 40, 0.25);
  overflow: hidden;
  z-index: 1100;
  text-align: left;
}

.notif-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #eef0f4;
  font-size: 15px;
}

.notif-link {
  border: 0;
  background: none;
  color: #e31e25;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.notif-panel__body { max-height: 60vh; overflow-y: auto; }

.notif-empty {
  padding: 28px 16px;
  text-align: center;
  color: #7a8090;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.notif-empty i { font-size: 30px; opacity: 0.6; }

.notif-item {
  display: flex;
  gap: 10px;
  padding: 11px 16px;
  border-bottom: 1px solid #f3f4f7;
  color: inherit;
  text-decoration: none;
}
.notif-item:hover { background: #f7f8fc; text-decoration: none; color: inherit; }
.notif-item.is-unread { background: #fff6f6; }
.notif-item.is-unread .notif-item__title { font-weight: 700; }

.notif-dot {
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  margin-top: 6px;
  border-radius: 50%;
  background: #3b82f6;
}
.notif-dot.lvl-success { background: #16a34a; }
.notif-dot.lvl-warning { background: #f59e0b; }
.notif-dot.lvl-danger { background: #dc2626; }

.notif-item__main { display: flex; flex-direction: column; min-width: 0; gap: 2px; }
.notif-item__title { font-size: 14px; font-weight: 500; }
.notif-panel__branch {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #eef1f6;
  color: #4b5160;
  font-size: 12px;
  font-weight: 500;
}
.notif-item__text {
  font-size: 13px;
  color: #4b5160;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.notif-item__meta { font-size: 11.5px; color: #8a90a0; }

.notif-panel__foot {
  display: block;
  padding: 11px 16px;
  text-align: center;
  font-size: 13.5px;
  font-weight: 600;
  color: #e31e25;
  background: #fafbfd;
}
.notif-panel__foot:hover { text-decoration: none; color: #b5161c; }

@media (max-width: 991.98px) {
  .notif-panel {
    position: fixed;
    top: calc(env(safe-area-inset-top, 0px) + 64px);
    left: 12px;
    right: 12px;
    width: auto;
    max-width: none;
  }
}
</style>
